import {assert} from 'Asserts';
import {AnyType} from 'data-types/benchling-type-def/AnyType';
import {BenchlingTypeDef, LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {COMPLEX_TYPE_TO_FACTORY, ComplexTypeOperator} from 'data-types/benchling-type-def/ComplexTypes';

// Keep these signatures in sync with the functions defined in benchling/benchling_expressions/function_definition

export interface BenchlingFunctionParameter {
  parameterName: string;
  spreadParameterName?: string;
  allowedType: BenchlingTypeDef;
  isRequired: boolean;
}

// Keep these function names in sync with any functions defined in benchling/benchling_expressions/function_definition
export enum BenchlingExpressionFunctionName {
  AND = 'AND',
  OR = 'OR',
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  HAS_ONE_OF = 'HAS_ONE_OF',
  HAS_ALL_OF = 'HAS_ALL_OF',
  HAS_NONE_OF = 'HAS_NONE_OF',
  EQUALS_IGNORE_ORDER = 'EQUALS_IGNORE_ORDER',
  NUMERIC_EQUALS = 'NUMERIC_EQUALS',
  NUMERIC_NOT_EQUALS = 'NUMERIC_NOT_EQUALS',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  LIST_CONTAINS_VALUE = 'LIST_CONTAINS_VALUE',
  NOT_LIST_CONTAINS_VALUE = 'NOT_LIST_CONTAINS_VALUE',
  SET_TIME_ON_DATETIME = 'SET_TIME_ON_DATETIME_V1',
  CALCULATE_DATETIME = 'CALCULATE_DATETIME_V1',
  HAS_SCHEMA = 'HAS_SCHEMA',
  NOT_HAS_SCHEMA = 'NOT_HAS_SCHEMA',
  IS_BLANK = 'IS_BLANK',
  IS_PRESENT = 'IS_PRESENT',
}

export interface BenchlingFunctionSignature {
  functionName: BenchlingExpressionFunctionName;
  parameters: Array<BenchlingFunctionParameter>;
  returnType: BenchlingTypeDef;
}

const BooleanFunctionOverListSignature = {
  parameters: [
    {
      parameterName: 'conditions',
      spreadParameterName: 'condition',
      allowedType: COMPLEX_TYPE_TO_FACTORY[ComplexTypeOperator.LIST](LeafType.BOOLEAN),
      isRequired: true,
    },
  ],
  returnType: LeafType.BOOLEAN,
};

export const AndFunctionSignature: BenchlingFunctionSignature = {
  ...BooleanFunctionOverListSignature,
  functionName: BenchlingExpressionFunctionName.AND,
};

export const OrFunctionSignature: BenchlingFunctionSignature = {
  ...BooleanFunctionOverListSignature,
  functionName: BenchlingExpressionFunctionName.OR,
};

const EqualityFunctionSignature = {
  parameters: [
    {
      parameterName: 'value1',
      allowedType: new AnyType(),
      isRequired: true,
    },
    {
      parameterName: 'value2',
      allowedType: new AnyType(),
      isRequired: true,
    },
  ],
  returnType: LeafType.BOOLEAN,
};

export const EqualsFunctionSignature: BenchlingFunctionSignature = {
  ...EqualityFunctionSignature,
  functionName: BenchlingExpressionFunctionName.EQUALS,
};
export const NotEqualsFunctionSignature: BenchlingFunctionSignature = {
  ...EqualityFunctionSignature,
  functionName: BenchlingExpressionFunctionName.NOT_EQUALS,
};

const SchemaComparisonFunctionSignature = {
  parameters: [
    {
      parameterName: 'value1',
      // Leaf types do not have inheritance, but in reality many things inherit from BenchlingObject
      // on the backend
      allowedType: LeafType.BENCHLING_OBJECT,
      isRequired: true,
    },
    {
      parameterName: 'value2',
      allowedType: LeafType.TEXT,
      isRequired: true,
    },
  ],
  returnType: LeafType.BOOLEAN,
};

export const HasSchemaFunctionSignature: BenchlingFunctionSignature = {
  ...SchemaComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.HAS_SCHEMA,
};
export const NotHasSchemaFunctionSignature: BenchlingFunctionSignature = {
  ...SchemaComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.NOT_HAS_SCHEMA,
};

const ListComparisonFunctionSignature = {
  parameters: [
    {
      parameterName: 'list1',
      allowedType: COMPLEX_TYPE_TO_FACTORY[ComplexTypeOperator.LIST](new AnyType()),
      isRequired: true,
    },
    {
      parameterName: 'list2',
      allowedType: COMPLEX_TYPE_TO_FACTORY[ComplexTypeOperator.LIST](new AnyType()),
      isRequired: true,
    },
  ],
  returnType: LeafType.BOOLEAN,
};

export const HasOneOfFunctionSignature: BenchlingFunctionSignature = {
  ...ListComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.HAS_ONE_OF,
};

export const HasAllOfFunctionSignature: BenchlingFunctionSignature = {
  ...ListComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.HAS_ALL_OF,
};

export const HasNoneOfFunctionSignature: BenchlingFunctionSignature = {
  ...ListComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.HAS_NONE_OF,
};

export const EqualsIgnoreOrderFunctionSignature: BenchlingFunctionSignature = {
  ...ListComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.EQUALS_IGNORE_ORDER,
};

const NumericComparisonFunctionSignature = {
  parameters: [
    {
      parameterName: 'value1',
      allowedType: COMPLEX_TYPE_TO_FACTORY[ComplexTypeOperator.UNION](LeafType.INTEGER, LeafType.DECIMAL),
      isRequired: true,
    },
    {
      parameterName: 'value2',
      allowedType: COMPLEX_TYPE_TO_FACTORY[ComplexTypeOperator.UNION](LeafType.INTEGER, LeafType.DECIMAL),
      isRequired: true,
    },
  ],
  returnType: LeafType.BOOLEAN,
};

export const NumericEqualsFunctionSignature: BenchlingFunctionSignature = {
  ...NumericComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.NUMERIC_EQUALS,
};

export const NumericNotEqualsFunctionSignature: BenchlingFunctionSignature = {
  ...NumericComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.NUMERIC_NOT_EQUALS,
};

export const GreaterThanFunctionSignature: BenchlingFunctionSignature = {
  ...NumericComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.GT,
};

export const GreaterThanOrEqualToFunctionSignature: BenchlingFunctionSignature = {
  ...NumericComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.GTE,
};

export const LessThanFunctionSignature: BenchlingFunctionSignature = {
  ...NumericComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.LT,
};

export const LessThanOrEqualToFunctionSignature: BenchlingFunctionSignature = {
  ...NumericComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.LTE,
};

export const SetTimeOnDatetimeFunctionSignature: BenchlingFunctionSignature = {
  parameters: [
    {
      parameterName: 'datetime',
      allowedType: LeafType.DATETIME,
      isRequired: true,
    },
    {
      parameterName: 'timezone',
      allowedType: LeafType.TEXT,
      isRequired: true,
    },
    {
      parameterName: 'hour',
      allowedType: LeafType.INTEGER,
      isRequired: true,
    },
    {
      parameterName: 'minute',
      allowedType: LeafType.INTEGER,
      isRequired: true,
    },
    {
      parameterName: 'second',
      allowedType: LeafType.INTEGER,
      isRequired: true,
    },
  ],
  returnType: LeafType.DATETIME,
  functionName: BenchlingExpressionFunctionName.SET_TIME_ON_DATETIME,
};

export const RelativeDatetimeFunctionSignature: BenchlingFunctionSignature = {
  parameters: [
    {
      parameterName: 'datetime',
      allowedType: LeafType.DATETIME,
      isRequired: true,
    },
    {
      parameterName: 'subtract_from_datetime',
      allowedType: LeafType.BOOLEAN,
      isRequired: true,
    },
    {
      parameterName: 'days',
      allowedType: LeafType.INTEGER,
      isRequired: false,
    },
    {
      parameterName: 'hours',
      allowedType: LeafType.INTEGER,
      isRequired: false,
    },
    {
      parameterName: 'minutes',
      allowedType: LeafType.INTEGER,
      isRequired: false,
    },
    {
      parameterName: 'seconds',
      allowedType: LeafType.INTEGER,
      isRequired: false,
    },
  ],
  returnType: LeafType.DATETIME,
  functionName: BenchlingExpressionFunctionName.CALCULATE_DATETIME,
};

const StringComparisonFunctionSignature = {
  parameters: [
    {
      parameterName: 'value1',
      allowedType: LeafType.TEXT,
      isRequired: true,
    },
    {
      parameterName: 'value2',
      allowedType: LeafType.TEXT,
      isRequired: true,
    },
  ],
  returnType: LeafType.BOOLEAN,
};

export const ContainsStringFunctionSignature: BenchlingFunctionSignature = {
  ...StringComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.CONTAINS,
};

export const NotContainsStringFunctionSignature: BenchlingFunctionSignature = {
  ...StringComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.NOT_CONTAINS,
};

// Checks whether a single scalar value (the left-hand lookup) is contained in a constant list of values (the
// right-hand parameter). Note the asymmetric parameter shape: `value` is a scalar and `targets` is a list.
const ListContainsValueComparisonFunctionSignature = {
  parameters: [
    {
      parameterName: 'value',
      allowedType: new AnyType(),
      isRequired: true,
    },
    {
      parameterName: 'targets',
      allowedType: COMPLEX_TYPE_TO_FACTORY[ComplexTypeOperator.LIST](new AnyType()),
      isRequired: true,
    },
  ],
  returnType: LeafType.BOOLEAN,
};

export const ListContainsValueFunctionSignature: BenchlingFunctionSignature = {
  ...ListContainsValueComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.LIST_CONTAINS_VALUE,
};

export const NotListContainsValueFunctionSignature: BenchlingFunctionSignature = {
  ...ListContainsValueComparisonFunctionSignature,
  functionName: BenchlingExpressionFunctionName.NOT_LIST_CONTAINS_VALUE,
};

const PresenceFunctionSignature = {
  parameters: [
    {
      parameterName: 'value1',
      allowedType: new AnyType(),
      isRequired: true,
    },
  ],
  returnType: LeafType.BOOLEAN,
};

export const IsBlankFunctionSignature: BenchlingFunctionSignature = {
  ...PresenceFunctionSignature,
  functionName: BenchlingExpressionFunctionName.IS_BLANK,
};

export const IsPresentFunctionSignature: BenchlingFunctionSignature = {
  ...PresenceFunctionSignature,
  functionName: BenchlingExpressionFunctionName.IS_PRESENT,
};

const BOOLEAN_CONNECTING_LABELS: Map<BenchlingExpressionFunctionName, string> = new Map([
  [BenchlingExpressionFunctionName.AND, 'and'],
  [BenchlingExpressionFunctionName.OR, 'or'],
]);

export function getLabelFromFunctionSignature(functionSignature: BenchlingFunctionSignature): string | null {
  const functionName = functionSignature.functionName;
  if (BOOLEAN_CONNECTING_LABELS.has(functionName)) {
    return BOOLEAN_CONNECTING_LABELS.get(functionName)!;
  }
  return null;
}

const DEFAULT_FUNCTION_NAMES: Record<BenchlingExpressionFunctionName, string | null> = {
  AND: 'all of',
  OR: 'any of',
  EQUALS: null,
  NOT_EQUALS: null,
  HAS_ONE_OF: 'has one of',
  HAS_ALL_OF: 'has all of',
  HAS_NONE_OF: 'has none of',
  EQUALS_IGNORE_ORDER: 'equals (ignoring order)',
  NUMERIC_EQUALS: 'equals',
  NUMERIC_NOT_EQUALS: 'does not equal',
  GT: 'is greater than',
  GTE: 'is greater than or equal to',
  LT: 'is less than',
  LTE: 'is less than or equal to',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'does not contain',
  LIST_CONTAINS_VALUE: 'is one of',
  NOT_LIST_CONTAINS_VALUE: 'is not one of',
  SET_TIME_ON_DATETIME_V1: 'set time on date',
  CALCULATE_DATETIME_V1: 'calculate date',
  HAS_SCHEMA: 'is of schema',
  NOT_HAS_SCHEMA: 'is not of schema',
  IS_BLANK: 'is blank',
  IS_PRESENT: 'is present',
};

export function getDisplayNameFromFunctionSignature(
  functionSignature: BenchlingFunctionSignature,
  valueType?: BenchlingTypeDef | null
): string {
  const functionName = functionSignature.functionName;
  const displayName = DEFAULT_FUNCTION_NAMES[functionName];
  if (displayName == null) {
    switch (functionName) {
      case BenchlingExpressionFunctionName.EQUALS:
        if (valueType === LeafType.DROPDOWN_OPTION) {
          return 'is';
        } else {
          return 'equals';
        }
      case BenchlingExpressionFunctionName.NOT_EQUALS:
        if (valueType === LeafType.DROPDOWN_OPTION) {
          return 'is not';
        } else {
          return 'does not equal';
        }
      default:
        return 'unimplemented display name';
    }
  }
  assert(displayName != null, 'Cannot get display name for function');
  return displayName;
}
